import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<'bug' | 'idea' | 'other'>('idea');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const { settings } = useSettings();
  const language = settings?.language || 'en';

  const labels = {
    en: {
      fab: 'Beta Feedback',
      title: 'Send Feedback',
      bug: '🐛 Bug',
      idea: '💡 Idea',
      other: '💬 Other',
      placeholderBug: 'Describe the bug — what happened and what you expected...',
      placeholderIdea: 'What feature or improvement would you love?',
      placeholderOther: "What's on your mind?",
      sending: 'Sending...',
      send: 'Send Feedback →',
      successTitle: 'Thanks for your feedback!',
      successMsg: 'It helps us make MyBalance better for everyone.',
      errorMsg: 'Failed to send feedback. Please try again.',
      note: 'Your feedback goes directly to the MyBalance team.',
    },
    fr: {
      fab: 'Retour bêta',
      title: 'Envoyer un commentaire',
      bug: '🐛 Bug',
      idea: '💡 Idée',
      other: '💬 Autre',
      placeholderBug: 'Décrivez le bug — ce qui s\'est passé et ce que vous attendiez...',
      placeholderIdea: 'Quelle fonctionnalité ou amélioration aimeriez-vous ?',
      placeholderOther: 'Qu\'avez-vous en tête ?',
      sending: 'Envoi en cours...',
      send: 'Envoyer →',
      successTitle: 'Merci pour votre retour !',
      successMsg: 'Cela nous aide à améliorer MyBalance pour tout le monde.',
      errorMsg: 'Échec de l\'envoi. Veuillez réessayer.',
      note: 'Votre retour va directement à l\'équipe MyBalance.',
    },
  };

  const lbl = labels[language as 'en' | 'fr'] || labels.en;

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    setError('');

    try {
      // Replace 'xpwzgkdo' with your actual Formspree form ID
      const response = await fetch('https://formspree.io/f/meelebyn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          feedbackType: type,
          email: user?.email || 'Anonymous',
          message,
          _subject: `[MyBalance Beta Feedback] ${type.charAt(0).toUpperCase() + type.slice(1)}`,
        }),
      });

      if (response.ok) {
        setSent(true);
        setTimeout(() => {
          setOpen(false);
          setSent(false);
          setMessage('');
          setType('idea');
        }, 2500);
      } else {
        setError(lbl.errorMsg);
      }
    } catch {
      setError(lbl.errorMsg);
    } finally {
      setSending(false);
    }
  };

  const placeholder =
    type === 'bug'
      ? lbl.placeholderBug
      : type === 'idea'
        ? lbl.placeholderIdea
        : lbl.placeholderOther;

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-40 right-4 z-40 flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2.5 text-xs font-bold text-white shadow-xl hover:opacity-90 transition-all active:scale-95 sm:bottom-24 sm:right-6"
        title={lbl.fab}
      >
        <span>💬</span>
        <span className="hidden sm:inline">{lbl.fab}</span>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-800">
            {sent ? (
              <div className="py-6 text-center">
                <p className="text-5xl mb-3">🎉</p>
                <h2 className="text-xl font-black text-slate-900 dark:text-white mb-1">{lbl.successTitle}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{lbl.successMsg}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-black text-slate-900 dark:text-white">{lbl.title}</h2>
                  <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xl">×</button>
                </div>

                {/* Type selector */}
                <div className="flex gap-2 mb-4">
                  {(['bug', 'idea', 'other'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setType(t)}
                      className={`flex-1 rounded-xl py-2 text-xs font-bold transition-all ${
                        type === t
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {lbl[t]}
                    </button>
                  ))}
                </div>

                {/* Message */}
                <textarea
                  placeholder={placeholder}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-700 dark:text-white resize-none"
                />

                {error && (
                  <p className="mt-2 text-xs text-red-500">{error}</p>
                )}

                <p className="mt-2 mb-4 text-xs text-slate-400">{lbl.note}</p>

                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || sending}
                  className="w-full min-h-[44px] rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 py-2.5 text-sm font-bold text-white shadow-lg hover:opacity-90 transition-all active:scale-95 disabled:opacity-50"
                >
                  {sending ? lbl.sending : lbl.send}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
